import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Phone, CalendarCheck } from "lucide-react";

export default function BookingModal({ open, onOpenChange }) {
  const { settings } = useSettings();
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", data: "", ora: "", persone: "2", note: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (settings.booking_external_url) { window.open(settings.booking_external_url, "_blank"); return; }
    setLoading(true);
    try {
      await api.post("/reservations", form);
      track("book_submit");
      toast.success("Richiesta di prenotazione inviata! Ti ricontatteremo per confermare.");
      onOpenChange(false);
      setForm({ nome: "", telefono: "", email: "", data: "", ora: "", persone: "2", note: "" });
    } catch (err) {
      toast.error("Si è verificato un errore. Prova a chiamarci.");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-cream border-brass/30 max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-bordeaux">Prenota un tavolo</DialogTitle>
          <DialogDescription className="font-accent text-lg text-oak/70 italic">
            Ti aspettiamo da Masetti.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-oak/70">Nome</Label>
            <Input required value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="booking-nome" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-oak/70">Telefono</Label>
              <Input required value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="booking-telefono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-oak/70">Persone</Label>
              <Input type="number" min="1" required value={form.persone} onChange={(e) => set("persone", e.target.value)} data-testid="booking-persone" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-oak/70">Email (facoltativa)</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-oak/70">Data</Label>
              <Input type="date" required value={form.data} onChange={(e) => set("data", e.target.value)} data-testid="booking-data" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-oak/70">Ora</Label>
              <Input type="time" required value={form.ora} onChange={(e) => set("ora", e.target.value)} data-testid="booking-ora" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-oak/70">Note (allergie, seggioloni…)</Label>
            <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="booking-note" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid="booking-submit">
            <CalendarCheck className="w-4 h-4 mr-2" /> {loading ? "Invio…" : "Invia richiesta"}
          </Button>
          {settings.phone_primary && (
            <a href={`tel:${settings.phone_primary.replace(/\s/g, "")}`} onClick={() => track("call_click")}
               className="flex items-center justify-center gap-2 text-sm text-oak/70 hover:text-bordeaux pt-1" data-testid="booking-call-link">
              <Phone className="w-4 h-4" /> oppure chiama {settings.phone_primary}
            </a>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
